/**
 * Collider binding (M10 P1a2, hull tiers spec §7 P1a2 + the collider half of its P1b2): every ship collides as the
 * model it shows.
 *
 * - **The flown hull.** The career flies the player on the Kestrel's def slot and `applyFit` rewrites that slot in
 *   place when you board another hull (`career/runtime.ts`), so the slot's `assetId` changes while
 *   `sim.shipColliders[slot]` and the player's body still hold the Kestrel's convex hulls. `syncPlayerCollider`
 *   notices the change (one string compare per call) and rebinds: the slot's collider and the player's body come
 *   from the stream's collider for the new asset (the stand-in's while it isn't loaded, P1b2), built once.
 * - **Late-landing models.** When the stream lands a model (`onLoaded`, rare), every def slot on that asset gets its
 *   collider, and every live entity on that asset whose body was built from something else (a stand-in, or the
 *   `def.colliderRadius` ball) is rebuilt with it, keeping `Pos64`, `Rot` and `Vel`.
 * - **A detached ship** (cruise, lane or tunnel: no body) only gets `sim.colliderOf[eid]` set; `attachShip` builds
 *   from it later. Calling `attachShip` here would re-attach a ship mid-travel. A dead ship likewise only records
 *   it: its respawn rebuilds the body from `colliderOf`.
 *
 * `flownDef: false` (`ship-views.json perHullView` off) leaves the player's slot and body as before M10. The career
 * runtime is untouched: its own `rebuildPlayerBody` still runs on a switch, and this fixes the collider in the same
 * tick or frame. Per-frame code (`syncPlayerCollider`) allocates nothing (D-014); the rest is rare.
 */
import type { Sim } from '../sim'
import { attachShip, detachShip } from '../sim'
import { Life, Ship } from '../world/components'
import type { ColliderData } from './loader'
import type { ShipStream } from './shipStream'

/** The stream calls the binder needs (structural, so tests can stub it). */
export type ColliderSource = Pick<ShipStream, 'colliderOf' | 'get' | 'request' | 'onLoaded'>

export interface ColliderBinderOptions {
  /** Bind the flown def's collider to the player (`ship-views.json perHullView`). */
  readonly flownDef: boolean
}

export interface ColliderBinder {
  readonly sim: Sim
  readonly ships: ColliderSource
  readonly flownDef: boolean
  /** The player entity and asset id the player's collider was last bound for (-1 / null: not yet). */
  boundEid: number
  boundAsset: string | null
  /** Stops listening to the stream. */
  dispose(): void
}

export function createColliderBinder(sim: Sim, ships: ColliderSource, opts: ColliderBinderOptions): ColliderBinder {
  const binder: ColliderBinder = {
    sim,
    ships,
    flownDef: opts.flownDef,
    boundEid: -1,
    boundAsset: null,
    dispose: () => {},
  }
  binder.dispose = ships.onLoaded((assetId) => bindLoadedAsset(binder, assetId))
  return binder
}

/**
 * Per tick and per frame (the switch commits while docked, when no tick runs): when the player entity or its slot's
 * asset changed since the last bind, rebinds the player's collider. A quiet call is two compares.
 */
export function syncPlayerCollider(b: ColliderBinder): void {
  if (!b.flownDef) return
  const p = b.sim.playerEid
  if (p < 0) return
  const def = b.sim.defs[Ship.defIndex[p]]
  if (def === undefined) return
  if (p === b.boundEid && def.assetId === b.boundAsset) return
  bindPlayer(b, p, Ship.defIndex[p], def.assetId)
}

/** Rare: the player's slot and body take `assetId`'s collider (asking the stream for the model when it isn't in). */
function bindPlayer(b: ColliderBinder, p: number, slot: number, assetId: string): void {
  if (b.ships.get(assetId) === undefined) b.ships.request([assetId])
  const collider = b.ships.colliderOf(assetId)
  b.sim.shipColliders[slot] = collider
  rebindEntity(b.sim, p, collider)
  b.boundEid = p
  b.boundAsset = assetId
}

/** Rare (`onLoaded`): every slot on `assetId` takes its collider; every entity on it is rebuilt when it differs. */
export function bindLoadedAsset(b: ColliderBinder, assetId: string): void {
  const sim = b.sim
  const collider = b.ships.colliderOf(assetId)
  const p = sim.playerEid
  // With the flown-def binding off, the player's slot and body stay as they were (pre-M10).
  const playerSlot = !b.flownDef && p >= 0 ? Ship.defIndex[p] : -1
  for (let i = 0; i < sim.defs.length; i++) {
    if (i !== playerSlot && sim.defs[i].assetId === assetId) sim.shipColliders[i] = collider
  }
  const ships = sim.ships.dense
  for (let k = 0; k < ships.length; k++) {
    const eid = ships[k]
    if (eid === p && !b.flownDef) continue
    if (sim.defs[Ship.defIndex[eid]]?.assetId === assetId) rebindEntity(sim, eid, collider)
  }
}

/**
 * Rare: ship `eid` collides with `collider` from now on. A living ship with a body is rebuilt in place (`Pos64`,
 * `Rot` and `Vel` kept, spin zeroed, as `attachShip` does); a detached or dead one only records it.
 */
export function rebindEntity(sim: Sim, eid: number, collider: ColliderData | null): void {
  if (sim.colliderOf[eid] === collider) return
  const hasBody = sim.physics.bodies[eid] !== undefined
  if (hasBody && Life.alive[eid] === 1) {
    detachShip(sim, eid)
    sim.colliderOf[eid] = collider
    attachShip(sim, eid)
    return
  }
  sim.colliderOf[eid] = collider
}
